export type MetricFormat = 'number' | 'money' | 'percent' | 'decimal';
export type Metric = {
  key: string;
  label: string;
  value: number | null;
  format: MetricFormat;
  definition: string;
  precision?: number;
};
export type Column = { key: string; label: string; format?: MetricFormat };
export type TrendRow = { date: string; value: number | null; secondary?: number | null };
export type Filters = { market: string; startDate: string; endDate: string };
export type FidelityFilters = Filters &
  Partial<
    Record<
      | 'itemStatus'
      | 'memberType'
      | 'memberFlag'
      | 'vendor'
      | 'product'
      | 'category'
      | 'channel'
      | 'priceBand'
      | 'priceBandBasis'
      | 'brand'
      | 'promoFlag'
      | 'promoCode'
      | 'saleType'
      | 'customerSegment'
      | 'offerCountry'
      | 'orderItemCategories'
      | 'renewalStatus'
      | 'subscriptionPeriod'
      | 'isPromo'
      | 'cancellationStatus'
      | 'promoSplit'
      | 'bracketMetric'
      | 'enrollmentMonth',
      string
    >
  >;
export type FidelityFilterControl = {
  key: keyof FidelityFilters;
  label: string;
  options: Array<string>;
  defaultValue: string;
  note?: string;
};

export type FidelityViewId =
  | 'overview'
  | 'takeup'
  | 'takeup-overview'
  | 'takeup-trend'
  | 'margin'
  | 'renewals'
  | 'cancellations'
  | 'cohorts';
export type FidelityCadence = 'day' | 'week' | 'month';
export type FidelityRow = Record<string, string | number | null>;
export type FidelitySeries = {
  key: string;
  label: string;
  color: string;
  dashed?: boolean;
  format?: MetricFormat;
  type?: 'line' | 'bar';
  axis?: 'left' | 'right';
};
export type FidelityPanel = {
  id: string;
  title: string;
  kind: 'metrics' | 'line' | 'bar' | 'composed' | 'table' | 'heatmap' | 'unknown';
  rows: Array<FidelityRow>;
  columns: Array<Column>;
  series: Array<FidelitySeries>;
  source: string;
  metrics?: Array<Metric>;
  note?: string;
  originalSheets?: Array<string>;
  span?: 'full' | 'half' | 'third' | 'fifth';
  height?: number;
  horizontal?: boolean;
  stacked?: boolean;
  stackMode?: 'count' | 'percent';
  facet?: string;
  initialSort?: { key: string; direction: 1 | -1 };
  total?: FidelityRow;
  valueFormat?: MetricFormat;
};
export type FidelityDashboard = {
  id: FidelityViewId;
  title: string;
  subtitle: string;
  source: string;
  panels: Array<FidelityPanel>;
  notes: Array<string>;
  missingFeatures: Array<string>;
  originalSheets: Array<string>;
};

// Saved XML palette mappings, documented in analysis/ORIGINAL_PALETTE.md.
// Neutral fallback strokes are labelled explicitly where no original encoding is proven.
export const FIDELITY_COLORS = {
  all: '#4e79a7',
  attached: '#f28e2b',
  actualRenewal: '#4e79a7',
  estimatedRenewal: '#4e79a7',
  executiveTotal: '#7e8188',
  executiveGmi: '#14b28c',
  added: '#76b7b2',
  lost: '#ff9da7',
  executiveNewFees: '#b07aa1',
  executiveRenewedFees: '#4e79a7',
  offerTotal: '#02b18b',
  subscription: '#3d56b3',
  offerGmi: '#7e8188',
  cancellation: '#465dbf',
  neutral: '#363a45',
  comparison: '#7e8188',
} as const;

const BOARD_NAMES: Record<FidelityViewId, string> = {
  overview: 'LP+ Executive Overview',
  takeup: 'LP+ Take Up Rate',
  'takeup-overview': 'LP+ Take Up Rate - Overview',
  'takeup-trend': 'LP+ Take Up Rate Trend',
  margin: 'LP+ Offer Margin Impact',
  renewals: 'LP+ Renewals',
  cancellations: 'LP+ Cancellations',
  cohorts: 'LP+ Cohorts',
};
export const FIDELITY_VIEWS = Object.entries(BOARD_NAMES).map(([id, label]) => ({ id: id as FidelityViewId, label }));
